import React, { createContext, useContext, useMemo } from 'react'

// Channel for content that changes its own rendered height outside the answer-text
// collapse/expand plumbing (e.g. a tweet card's "more" toggle). Under static geometry the
// reserved row height is frozen per content signature, so any in-place growth must be
// reported here for the provider to bump the row's geometry revision — which re-estimates the
// reserve and applies it through the virtualizer's resize channel instead of leaving the
// content to spill into (or be clipped by) the pinned row frame.

export type TweetExpandedChange = {
    questId?: string | number | null
    answerId?: string | number | null
    expanded: boolean
}

export type AnswerEditingChange = {
    questId?: string | number | null
    answerId?: string | number | null
    editing: boolean
}

export type RowGeometryEvents = {
    notifyTweetExpandedChange: (change: TweetExpandedChange) => void
    // The provider owns the expansion state the estimator reserves from; consumers render
    // from it too, so a row remount (v1 key rotation, v2 window exit/re-entry) can never
    // leave collapsed content inside an expanded reserve.
    isTweetExpanded: (answerId: string | number | null | undefined) => boolean
    // Inline editing swaps in a composer far taller than the read-mode reserve; the provider
    // lifts the row's clipping and (v2) resizes the reserve while editing is active.
    notifyAnswerEditingChange: (change: AnswerEditingChange) => void
}

const NOOP_EDITING_CHANGE = () => {}

// Exported for class components (Answer) that consume via contextType.
export const RowGeometryEventsContext = createContext<RowGeometryEvents | null>(null)

export function RowGeometryEventsProvider({
    questId = null,
    expandedTweetAnswerIds,
    onTweetExpandedChange,
    onAnswerEditingChange = NOOP_EDITING_CHANGE,
    children,
}: {
    questId?: string | number | null
    expandedTweetAnswerIds?: Array<string | number> | Set<string> | null
    onTweetExpandedChange: (change: TweetExpandedChange) => void
    onAnswerEditingChange?: (change: AnswerEditingChange) => void
    children: React.ReactNode
}) {
    const value = useMemo<RowGeometryEvents>(() => {
        const expandedIds = new Set(Array.from(expandedTweetAnswerIds || [], id => String(id)))
        return {
            notifyTweetExpandedChange: ({ answerId, expanded }) =>
                onTweetExpandedChange({ questId, answerId, expanded }),
            isTweetExpanded: answerId => answerId != null && expandedIds.has(String(answerId)),
            notifyAnswerEditingChange: ({ answerId, editing }) =>
                onAnswerEditingChange({ questId, answerId, editing }),
        }
    }, [expandedTweetAnswerIds, onAnswerEditingChange, onTweetExpandedChange, questId])

    return (
        <RowGeometryEventsContext.Provider value={value}>
            {children}
        </RowGeometryEventsContext.Provider>
    )
}

export function useRowGeometryEvents(): RowGeometryEvents | null {
    return useContext(RowGeometryEventsContext)
}
